import { useState } from 'react';
import { Alert, Button, Field, Input, formatDateTime } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

/** Platform administrator accounts (docs/07 "/staff"). */

interface PlatformStaff {
  id: string;
  email: string;
  name: string;
  active: boolean;
  pending: boolean;
  lastLoginAt: string | null;
}

export function Staff() {
  const t = useT();
  const { user } = useSession();
  const staff = useResource<PlatformStaff[]>('/platform/staff');
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const columns: Column<PlatformStaff>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => (
        <div>
          <p className="font-medium">
            {row.name}
            {row.id === user?.id && <span style={{ color: 'var(--color-faint)' }}> · you</span>}
          </p>
          <p className="text-xs" style={{ color: 'var(--color-faint)' }}>
            {row.email}
          </p>
        </div>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last signed in',
      render: (row) =>
        row.lastLoginAt ? (
          <span className="tabular">{formatDateTime(row.lastLoginAt, 'UTC')}</span>
        ) : (
          <span style={{ color: 'var(--color-faint)' }}>never</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span
          style={{
            color: row.pending
              ? 'var(--signal-delayed)'
              : row.active
                ? 'var(--signal-departed)'
                : 'var(--color-faint)',
          }}
        >
          {row.pending ? 'Invitation sent' : row.active ? 'Active' : 'Deactivated'}
        </span>
      ),
    },
  ];

  async function invite() {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.post('/platform/staff', form);
      setNotice(`Invitation issued to ${form.email}.`);
      setForm({ email: '', name: '' });
      setInviting(false);
      await staff.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not send that invitation.'));
    } finally {
      setBusy(false);
    }
  }

  async function toggle(member: PlatformStaff) {
    setError(null);
    try {
      await api.put(`/platform/staff/${member.id}`, { active: !member.active });
      await staff.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not update that account.'));
    }
  }

  return (
    <Page
      title={t('staff.titleLong')}
      description={t('staff.subtitleLong')}
      actions={<Button onClick={() => setInviting(true)}>Invite administrator</Button>}
    >
      {notice && (
        <div className="mb-4">
          <Alert tone="success">{notice}</Alert>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={staff.data}
        rowKey={(row) => row.id}
        loading={staff.loading}
        error={staff.error}
        empty={{ title: 'No administrators', description: 'Invite someone to help run the platform.' }}
      />

      {staff.data && staff.data.length > 1 && (
        <div className="mt-4 flex flex-col gap-2">
          {staff.data
            .filter((member) => member.id !== user?.id)
            .map((member) => (
              <div key={member.id} className="flex items-center gap-3 text-sm">
                <span className="w-64 truncate" style={{ color: 'var(--color-muted)' }}>
                  {member.name} · {member.email}
                </span>
                <Button size="sm" variant="secondary" onClick={() => toggle(member)}>
                  {member.active ? 'Deactivate' : 'Reactivate'}
                </Button>
              </div>
            ))}
        </div>
      )}

      <Drawer
        open={inviting}
        title={t('staff.inviteTitle')}
        onClose={() => setInviting(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setInviting(false)}>
              Cancel
            </Button>
            <Button onClick={invite} loading={busy} disabled={!form.email || !form.name}>
              Send invitation
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Alert tone="warning">
            A platform administrator can see and change every tenant on the platform.
          </Alert>
          <Field label={t('common.name')} htmlFor="p-name">
            <Input id="p-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label={t('common.email')} htmlFor="p-email">
            <Input
              id="p-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
        </div>
      </Drawer>
    </Page>
  );
}
